import type { LocationChangedPayload, OpenLinkPayload } from '@publicala/delfino';
import { hostBridge } from '@publicala/delfino';
import { throttle } from 'lodash';
import { isE2EMockActive } from '../e2eMockGuard';

const LOCATION_CHANGE_THROTTLE_MS = 1000;

/**
 * Navigation API for Volpe.
 * Provides methods for navigation actions via hostBridge.
 */
class NavigationAPI {
    private throttledLocationChanged = throttle(
        (payload: LocationChangedPayload) => {
            if (isE2EMockActive()) {
                return;
            }
            hostBridge.navigation.locationChanged(payload);
        },
        LOCATION_CHANGE_THROTTLE_MS,
        { leading: true, trailing: true }
    );

    /**
     * Open an external link. The callers answer the tap before the host does,
     * so a refusal (a bridge not yet connected, a host without the handler) is
     * logged with the link instead of surfacing as an unhandled rejection.
     * Skipped in E2E standalone mode to prevent errors when hostBridge is not initialized.
     */
    async openLink(payload: OpenLinkPayload): Promise<void> {
        if (isE2EMockActive()) {
            return;
        }
        try {
            await hostBridge.navigation.openLink(payload);
        } catch (error) {
            console.warn('[NavigationAPI] the host did not open the link:', payload.url, error);
        }
    }

    /**
     * Notify that the location changed.
     * Throttled to avoid rate limiting (max 1 call per second).
     * Skipped in E2E standalone mode to prevent errors when hostBridge is not initialized.
     */
    async locationChanged(payload: LocationChangedPayload): Promise<void> {
        this.throttledLocationChanged(payload);
    }

    /**
     * Notify that the user reached the last page.
     * Skipped in E2E standalone mode to prevent errors when hostBridge is not initialized.
     */
    async reachLastPage(): Promise<void> {
        if (isE2EMockActive()) {
            return;
        }
        return hostBridge.navigation.reachLastPage();
    }
}

export const navigationAPI = new NavigationAPI();
